// AutoHQ Care+ dedicated landing page at /autohq-care-plus
// Purpose: Convert paid ad traffic (Meta + Google) into Care+ purchases.
// Fires the full funnel: PageView → ViewContent → AddToCart →
// InitiateCheckout → Lead (OTP verify) → Purchase.

import { useEffect, useRef, useState } from "react";
import { Shield, Wrench, Award } from "lucide-react";

import CPHero from "@/components/care-plus-landing/CPHero";
import CPPricing from "@/components/care-plus-landing/CPPricing";
import CPSavings from "@/components/care-plus-landing/CPSavings";
import CPHowItWorks from "@/components/care-plus-landing/CPHowItWorks";
import CPTestimonials from "@/components/care-plus-landing/CPTestimonials";
import CPFaq from "@/components/care-plus-landing/CPFaq";
import CPStickyCta from "@/components/care-plus-landing/CPStickyCta";
import CPCheckoutModal from "@/components/care-plus-landing/CPCheckoutModal";

import { trackViewContent, trackAddToCart } from "@/lib/pixel";
import { captureAttribution } from "@/lib/utm";
import { getOrAssignVariant } from "@/lib/abTest";
import { waLink } from "@/lib/api";

// Pseudo-slot counter — same value across the day, drops slightly each day
// to feel believable. Driven by date so all visitors see the same number.
const slotsLeftToday = () => {
  const seed = new Date().getDate();
  return 50 - ((seed * 7) % 18); // ~32-50 range
};

export default function CarePlusLanding() {
  const [open, setOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState("care_plus_12m");
  const [variant, setVariant] = useState("a");
  const savingsRef = useRef(null);
  const atcFired = useRef(false);
  const slotsLeft = slotsLeftToday();

  // Capture UTM/click-ids on landing, assign A/B variant, fire PageView/ViewContent
  useEffect(() => {
    captureAttribution();
    const v = getOrAssignVariant();
    setVariant(v);
    trackViewContent({ source: "care_plus_offer_lp", variant: v });
    // Set page-specific SEO meta tags
    document.title = "AutoHQ Care+ | Save ₹10,000+ on Car Care for ₹1,249 — Hyderabad";
    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith("link") ? "link" : "meta");
        const [, name] = selector.match(/\[(?:name|property|rel)="([^"]+)"\]/) || [];
        if (name) {
          if (selector.startsWith("link")) el.setAttribute("rel", name);
          else el.setAttribute(selector.includes("property=") ? "property" : "name", name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content",
      "AutoHQ Care+ annual package — 20+ services (washes, AC, alignment, denting, labour off) worth ₹10,000+ for just ₹1,249. Genuine parts, multi-brand. Activate in 60 seconds.");
    setMeta('link[rel="canonical"]', "href", "https://autohq.in/autohq-care-plus");
    setMeta('meta[property="og:title"]', "content", "AutoHQ Care+ | ₹10,000 of car care for ₹1,249");
    setMeta('meta[property="og:description"]', "content",
      "20+ services across washes, AC, alignment, denting & labour. 100% genuine parts. 1-year validity. ₹1,249 only.");
    setMeta('meta[property="og:url"]', "content", "https://autohq.in/autohq-care-plus");
    // Inject Product JSON-LD once
    if (!document.getElementById("cp-ld-json")) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "cp-ld-json";
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "AutoHQ Care+ Annual Package",
        "image": "https://autohq.in/logo-on-light.png",
        "description": "Annual car-care membership with 20+ redemptions across washes, AC, alignment, denting and labour discounts.",
        "brand": { "@type": "Brand", "name": "AutoHQ" },
        "offers": {
          "@type": "Offer",
          "url": "https://autohq.in/autohq-care-plus",
          "priceCurrency": "INR",
          "price": "1249",
          "availability": "https://schema.org/InStock",
        },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "1200" },
      });
      document.head.appendChild(script);
    }
    return () => {
      const ld = document.getElementById("cp-ld-json");
      if (ld) ld.remove();
    };
  }, []);

  // Fire AddToCart when user scrolls to the savings/pricing block (intent signal)
  useEffect(() => {
    if (!savingsRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !atcFired.current) {
            atcFired.current = true;
            trackAddToCart({ source: "care_plus_offer_lp" });
            obs.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );
    obs.observe(savingsRef.current);
    return () => obs.disconnect();
  }, []);

  const onBuy = (pkgId = "care_plus_12m") => {
    setSelectedPkg(pkgId);
    setOpen(true);
  };

  return (
    <div className="bg-white text-[#0A0A0A] min-h-screen">
      {/* Top bar — minimal: logo + login link only. No nav distractions. */}
      <header className="bg-[#0A0A0A] text-white px-5 lg:px-8 py-3 border-b border-white/10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/" data-testid="cp-logo-home" className="flex items-center gap-2">
            <img src="/logo-on-light.png" alt="AutoHQ" className="h-7 brightness-0 invert" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/50 hidden sm:inline">
              Ranigunj, Hyderabad
            </span>
          </a>
          <a
            href="/me"
            data-testid="cp-existing-member"
            className="text-xs uppercase tracking-wider text-white/70 hover:text-[#D4AF37] transition-colors"
          >
            Member login →
          </a>
        </div>
      </header>

      <CPHero onBuyClick={() => onBuy("care_plus_12m")} slotsLeft={slotsLeft} variant={variant} />

      {/* Trust strip */}
      <section className="bg-white border-b border-[#E4E4E7] px-5 lg:px-8 py-6">
        <div className="max-w-6xl mx-auto grid grid-cols-3 gap-3 text-center">
          {[
            { i: Shield,  t: "100% Genuine Parts", s: "OEM across all brands" },
            { i: Wrench,  t: "Multi-Brand Experts", s: "Maruti, Hyundai, Tata, Honda…" },
            { i: Award,   t: "1,200+ Happy Members", s: "Rated 4.8 / 5 on Google" },
          ].map((b, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5 sm:flex-row sm:gap-3 sm:justify-center">
              <b.i className="w-5 h-5 text-[#D4AF37]" />
              <div className="text-left">
                <p className="text-xs sm:text-sm font-bold leading-none">{b.t}</p>
                <p className="text-[10px] sm:text-xs text-[#71717A] mt-1 leading-none">{b.s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div ref={savingsRef}>
        <CPSavings onBuyClick={() => onBuy("care_plus_12m")} />
      </div>

      <CPPricing onBuyClick={(pkgId) => onBuy(pkgId)} />

      <CPHowItWorks />
      <CPTestimonials />
      <CPFaq />

      {/* Final CTA block */}
      <section data-testid="cp-final-cta" className="bg-[#0A0A0A] text-white px-5 lg:px-8 py-16 sm:py-20 text-center">
        <div className="max-w-2xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Last call</p>
          <h2 className="mt-3 text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
            Three plans. From <span className="text-[#D4AF37]">₹549</span>.
            <br />
            <span className="text-white/70">Pick yours.</span>
          </h2>
          <p className="mt-4 text-base text-white/70">
            3 months · 6 months · 12 months — start saving on car care today.
          </p>
          <button
            data-testid="cp-final-cta-btn"
            onClick={() => { const el = document.getElementById("pricing"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
            className="mt-7 inline-block bg-[#D4AF37] hover:bg-[#E5C254] text-[#0A0A0A] font-extrabold text-base px-10 py-5 rounded-sm uppercase tracking-wider"
          >
            Compare plans →
          </button>
          <p className="mt-4 text-xs text-white/40">No subscription · No auto-renewal · 7-day refund</p>
        </div>
      </section>

      {/* Mini footer */}
      <footer className="bg-[#FAFAFA] border-t border-[#E4E4E7] px-5 lg:px-8 py-8 text-center text-xs text-[#71717A]">
        <p>
          © {new Date().getFullYear()} AutoHQ · Multi-Brand Car Service, Ranigunj, Hyderabad ·{" "}
          <a href="/" className="text-[#0A0A0A] hover:underline">Home</a> ·{" "}
          <a href="/me" className="text-[#0A0A0A] hover:underline">Members</a>
        </p>
      </footer>

      {/* Sticky WhatsApp + buy bar */}
      <a
        data-testid="cp-whatsapp-float"
        href={waLink("Hi AutoHQ! I want to buy the Care+ package (₹1,249).")}
        target="_blank" rel="noopener noreferrer"
        className="fixed bottom-20 right-4 z-40 w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
        aria-label="WhatsApp"
      >
        <svg viewBox="0 0 32 32" className="w-6 h-6 fill-white">
          <path d="M16 .8C7.6.8.8 7.6.8 16c0 2.7.7 5.3 2 7.6L.8 31.2l7.8-2c2.2 1.2 4.7 1.9 7.4 1.9 8.4 0 15.2-6.8 15.2-15.2S24.4.8 16 .8zm0 27.7c-2.4 0-4.7-.6-6.7-1.8l-.5-.3-4.6 1.2 1.2-4.5-.3-.5c-1.3-2-2-4.4-2-6.8C3.1 8.9 8.9 3.1 16 3.1S28.9 8.9 28.9 16 23.1 28.5 16 28.5z" />
        </svg>
      </a>

      <CPStickyCta onBuyClick={() => onBuy("care_plus_12m")} />
      <CPCheckoutModal open={open} onClose={() => setOpen(false)} packageId={selectedPkg} />
    </div>
  );
}
